// Locale-correct unit words. EN has two forms, PL has three (1 / 2–4 / 5+, with
// the 12–14 exception) — so every count-bearing sentence has to inflect its unit
// or it reads as machine translation.

/** The three plural buckets our dictionaries use as key suffixes. */
export type PluralForm = 'one' | 'few' | 'many'

/**
 * Which `_one` / `_few` / `_many` key a count needs.
 *
 * WHY THIS EXISTS: `t()` is a flat dotted lookup with no plural selection (see
 * lib/i18n.tsx), so the CALLER picks the key suffix. Without one shared function
 * every screen re-implements Polish plurals — and the 12–14 exception is exactly
 * the part people get wrong, so you end up with "12 edity" on one screen and
 * "12 editów" on another. One implementation, used everywhere:
 *
 *     const k = `appPackQa.bulk.selected_${pluralForm(locale, n)}`
 *     t(k, { count: n })
 *
 * English keeps the identical key set (parity test), with `few` and `many`
 * holding the same string — so this returns a valid form for both languages and
 * callers never branch on locale.
 */
export function pluralForm(locale: 'en' | 'pl', n: number): PluralForm {
  const abs = Math.abs(n)
  if (locale === 'en') return abs === 1 ? 'one' : 'many'
  if (abs === 1) return 'one'
  const mod10 = abs % 10
  const mod100 = abs % 100
  if (mod10 >= 2 && mod10 <= 4 && !(mod100 >= 12 && mod100 <= 14)) return 'few'
  return 'many'
}

/** Unit word for a gem count (UI render-currency rebrand). */
export function gemWord(locale: 'en' | 'pl', n: number): string {
  if (locale === 'en') return n === 1 ? 'gem' : 'gems'
  const form = pluralForm('pl', n)
  return form === 'one' ? 'gem' : form === 'few' ? 'gemy' : 'gemów'
}

/** Unit word for an edit count ("1 edit" / "3 edity" / "10 editów"). */
export function editWord(locale: 'en' | 'pl', n: number): string {
  if (locale === 'en') return n === 1 ? 'edit' : 'edits'
  const form = pluralForm('pl', n)
  return form === 'one' ? 'edit' : form === 'few' ? 'edity' : 'editów'
}

/**
 * Unit word for a token count ("1 token" / "3 tokeny" / "10 tokenów").
 *
 * Reguła 34 (MINIMAL-2026-09-18, decyzja Domiego 20.09): słowem waluty w języku produktu jest TOKEN.
 * `gemWord` wyżej żyje do chwili, gdy ostatni ekran przejdzie na nowe słowo, i ma umrzeć razem z nim.
 */
export function tokenWord(locale: 'en' | 'pl', n: number): string {
  if (locale === 'en') return n === 1 ? 'token' : 'tokens'
  const form = pluralForm('pl', n)
  return form === 'one' ? 'token' : form === 'few' ? 'tokeny' : 'tokenów'
}
